import { c as _c } from "react/compiler-runtime";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { tableTypeSelector } from "@store/table/table-selector";
import { removeOneAfterFetching, removeOneManually, setBulkActionName } from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo } from "react";
import { Text, ResendEmailIcon, useToast } from "@flat-ui";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
/**
 * Resend invitation in bulk to the unregistered members of:
 * projects when project is available and component rendered under the team tab of project overview,
 */
export function BulkResendInvitation(t0) {
  const $ = _c(24);
  const {
    selectedEntities,
    companyId,
    projectId,
    subjectType
  } = t0;
  const dispatch = useAppDispatch();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const tableType = useAppSelector(tableTypeSelector);
  const mode = useBulkActionsMode();
  let t1;
  bb0: switch (subjectType) {
    case "workspace":
    case "group":
    case "team":
      {
        t1 = "Resend invitation to workspace";
        break bb0;
      }
    case "project":
      {
        t1 = "Resend invitation to project";
        break bb0;
      }
    default:
      {
        t1 = undefined;
      }
  }
  const tooltipText = t1;
  let t2;
  if ($[0] !== companyId || $[1] !== dispatch || $[2] !== openToast || $[3] !== projectId || $[4] !== selectedEntities || $[5] !== subjectType || $[6] !== tableType || $[7] !== trackEvent) {
    t2 = async function onResendInvitationClick() {
      const bulkActionResults = {
        success: 0,
        failed: 0
      };
      if (tableType) {
        trackEvent({
          name: BulkActionEvents.bulkResendInvitation,
          props: {
            dataType: tableType,
            numberOfEntities: selectedEntities.length
          }
        });
      }
      dispatch(setBulkActionName("resendInvitation"));
      const resendInvitationPromises = selectedEntities.map(member => {
        if (FaroCoreApiUtils.Member.isMemberActive(member)) {
          dispatch(removeOneManually(member.identity));
          return "skip";
        }
        if (projectId && subjectType === "project") {
          return faroCoreApiClient.projects.resendProjectMembershipInvite({
            companyId,
            projectId,
            email: member.email
          }).then(() => {
            dispatch(removeOneAfterFetching(member.identity));
          });
        } else {
          if (subjectType === "workspace" || subjectType === "group" || subjectType === "team") {
            return faroCoreApiClient.company.resendCompanyMembershipInvite({
              companyId,
              userIdentity: member.email
            }).then(() => {
              dispatch(removeOneAfterFetching(member.identity));
            });
          }
        }
      });
      await Promise.allSettled(resendInvitationPromises).then(results => {
        results.forEach(result => {
          if (result.status === "fulfilled" && result.value !== "skip") {
            bulkActionResults.success = bulkActionResults.success + 1;
          } else {
            if (result.status === "rejected") {
              bulkActionResults.failed = bulkActionResults.failed + 1;
              const error = result.reason;
              openToast({
                title: getErrorDisplayMarkup(error),
                variant: "error"
              });
            }
          }
        });
      });
      if (bulkActionResults.failed === 0) {
        openToast({
          title: "Invitations sent"
        });
      } else {
        if (bulkActionResults.success > 0) {
          openToast({
            title: `${bulkActionResults.success} of ${bulkActionResults.success + bulkActionResults.failed} invitations sent`,
            variant: "info"
          });
        }
      }
      dispatch(setBulkActionName(null));
    };
    $[0] = companyId;
    $[1] = dispatch;
    $[2] = openToast;
    $[3] = projectId;
    $[4] = selectedEntities;
    $[5] = subjectType;
    $[6] = tableType;
    $[7] = trackEvent;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const onResendInvitationClick = t2;
  const t3 = tooltipText ?? "";
  let t4;
  if ($[9] !== mode || $[10] !== t3) {
    t4 = getBulkActionButtonConfig(mode, t3);
    $[9] = mode;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const config = t4;
  let t5;
  if ($[12] !== config.sx) {
    t5 = {
      ...config.sx
    };
    $[12] = config.sx;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ResendEmailIcon sx={{
      width: "18px",
      height: "18px"
    }} />;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== config.shouldShowText) {
    t7 = config.shouldShowText && <Text variant="buttonM">Resend invitation</Text>;
    $[15] = config.shouldShowText;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== config.loadingTrackColor || $[18] !== config.rippleColor || $[19] !== config.tooltipText || $[20] !== onResendInvitationClick || $[21] !== t5 || $[22] !== t7) {
    t8 = <FaroTextButton onClick={onResendInvitationClick} tooltipText={config.tooltipText} sx={t5} startIcon={t6} rippleColor={config.rippleColor} loadingTrackColor={config.loadingTrackColor}>{t7}</FaroTextButton>;
    $[17] = config.loadingTrackColor;
    $[18] = config.rippleColor;
    $[19] = config.tooltipText;
    $[20] = onResendInvitationClick;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}